import {Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {FormsModule} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {environment} from '../../../environments/environment';
import {PaymentDetailsService} from '../../shared/services/payment-details.service';
import {CaptchaService} from '../../shared/services/captcha.service';
import {VirtualKeyboardComponent} from '../../components/virtual-keyboard/virtual-keyboard.component';
import {AppConstants} from '../../shared/app-constants';
import {ErrorBannerComponent} from '../../components/error-banner/error-banner.component';
import {CustomModalComponent} from '../../components/custom-modal/custom-modal.component';
import {PaymentTypeMapping} from '../../models/payment-method.model';
import {AuthService} from '../../shared/services/auth.service';

declare const grecaptcha: any;

@Component({
  selector: 'app-retrieve-court-payments',
  standalone: true,
  imports: [CommonModule, FormsModule, VirtualKeyboardComponent, ErrorBannerComponent, CustomModalComponent],
  templateUrl: './retrieve-court-payments.component.html',
  styleUrl: './retrieve-court-payments.component.scss'
})
export class RetrieveCourtPaymentsComponent implements OnInit {
  paymentReferenceNumber: string = '';
  payerID: string = '';
  captchaError = false;
  paymentType: string | null = null;
  siteKey = environment.recaptcha.siteKey;
  isIdNumberValid: string = '';
  isPacNumberInvalid: boolean = false;
  invalidPacNumberError: string = '';
  pacErrorMessage: string = '';
  skipCaptcha = false;
  showMismatchConfirmation = false;
  paymentConfirmationMessage: string = "";
  captchaErrorMessage: string = "";
  showConfirmOnly: boolean = false;
  cachedPacResponse: any = null;
  isTerminal: boolean = false;
  @ViewChild('paymentConfirmationModalRef') paymentConfirmationModalRef!: CustomModalComponent;
  paymentTypeMapping = PaymentTypeMapping;
  note: string = AppConstants.RETRIEVE_PAYMENT_NOTE;
  checkNote: string = AppConstants.CHEQUE_NOTE1;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly paymentDetailsService: PaymentDetailsService,
    private readonly captchaService: CaptchaService,
    private readonly authService: AuthService
  ) {
  }

  ngOnInit() {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${environment.recaptcha.siteKey}`;
    document.head.appendChild(script);
    this.paymentType = history?.state?.paymentType;
    const authDataRaw = localStorage.getItem('authData');
    if (authDataRaw) {
      const authData = JSON.parse(authDataRaw);
      this.isTerminal = authData?.accessContext === "TERMINAL";
    }
  }

  onSubmit() {
    this.captchaError = false;
    this.isIdNumberValid = '';

    if (this.skipCaptcha) {
      this.fetchDetails();
      return;
    }

    if (grecaptcha) {
      grecaptcha.ready(() => {
        grecaptcha.execute(this.siteKey, {action: 'submit'}).then((token: string) => {
          this.captchaService.verifyToken(token).subscribe({
            next: (res) => {
              if (res.success && typeof res.score === 'number' && res.score >= 0.5) {
                this.fetchDetails();
              } else {
                this.captchaError = true;
              }
            },
            error: () => {
              this.captchaErrorMessage = AppConstants.CAPTCHA_ERROR_MESSAGE;
            }
          });
        });
      });
    }
  }

  fetchDetails() {
    const payerId = this.payerID.toLocaleUpperCase();
    this.paymentDetailsService.fetchDetails(this.paymentReferenceNumber, payerId).subscribe({
      next: (response: any) => {
        const errorMsg = response?.fetchPaymentInfoReturn?.errorMessage || response?.errorMessage;

        if (errorMsg) {
          const isConnectionRefused = errorMsg.includes('Connection refused');
          if (isConnectionRefused) {
            this.pacErrorMessage = AppConstants.ERROR_SERVICE_UNAVAILABLE;
          }

          // Set generic message only if it's NOT an invalid PAC or connection error
          if (!isConnectionRefused) {
            this.pacErrorMessage = errorMsg;
          }
          return;
        }

        response = {
          ...response,
          pacNumber: this.paymentReferenceNumber,
          paymentCategory: 'COURT_PAYMENT'
        };

        const actualType = response?.fetchPaymentInfoReturn?.wspaymentAdvice?.paymentType;
        const expectedTypes = this.paymentTypeMapping[this.paymentType ?? ''];
        const actualMappedType = this.getMappedPaymentTypeName(actualType);

        if (actualType && expectedTypes && expectedTypes !== actualMappedType) {
          const expectedTypeLabel = this.paymentTypeMapping[this.paymentType ?? ''] || this.paymentType;
          this.paymentConfirmationMessage = `You have selected payment for ${expectedTypeLabel} but entered the details of ${actualMappedType}. Do you want to proceed with the payment of the ${actualMappedType}?`;
          this.showConfirmOnly = false;
          this.cachedPacResponse = response;

          if (this.paymentConfirmationModalRef) {
            this.paymentConfirmationModalRef.showModal();
          }
        } else {
          this.router.navigate(['court-payment-details'], {
            state: {pacDetails: response}
          });
        }
      },
      error: (err) => {
        const connectionError = err?.error?.error?.message || err?.error?.message || err?.message || '';
        // Handled Error when Konglet is unavailable
        if (connectionError.includes('Connection refused')) {
          this.pacErrorMessage = AppConstants.ERROR_SERVICE_UNAVAILABLE;
        }
      }
    });
  }


  onClick() {
    this.router.navigate(['']);
  }


  getMappedPaymentTypeName(code: string): string {
    if (this.paymentTypeMapping[code]) {
      return this.paymentTypeMapping[code];
    }
    return code;
  }

  handleConfirmationModalClose(confirmed: boolean) {
    this.paymentConfirmationModalRef.hideModal();

    if (confirmed && this.cachedPacResponse) {
      this.router.navigate(['court-payment-details'], {
        state: {pacDetails: this.cachedPacResponse}
      });
    } else {
      this.router.navigate(['']);
    }
    this.paymentConfirmationMessage = '';
    this.showConfirmOnly = true;
    this.cachedPacResponse = null;
  }

  validatePacNumber(value: string): void {
    const isPac = value.startsWith('PAC');
    const pacPattern = /^PAC\d{9}$/;
    const normalPattern = /^\d{9}$/;
    const isValid = isPac ? pacPattern.test(value) : normalPattern.test(value);
    if (isValid) {
      this.isPacNumberInvalid = false;
      this.invalidPacNumberError = '';
    } else {
      this.isPacNumberInvalid = true;
      this.invalidPacNumberError = AppConstants.ERROR_INVALID_PAC_NUMBER;
    }
  }

  clearPacInfoErrorMessages() {
    this.isPacNumberInvalid = false;
    this.isIdNumberValid = '';
    this.pacErrorMessage = '';
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    this.clearPacInfoErrorMessages();
    const pastedText = event.clipboardData?.getData('text') || '';
    let finalText;
    if (pastedText.startsWith('PAC')) {
      finalText = pastedText.slice(0, 12);
    } else {
      finalText = pastedText.slice(0, 9);
    }
    this.paymentReferenceNumber = finalText;
  }

  removeSpacesOnPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    this.payerID = pastedText.replaceAll(/\s/g, '');
  }

}
